import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import NewNavbar from "../components/NewNavbar";
import BoardNameModal from "../components/BoardNameModal";
import FloatingChatBar from "../components/FloatingChatBar";
import { useMarketplaceChat } from "../context/MarketplaceChatProvider.js";
import AuthModal from "../components/AuthModal";
import SEO from "../components/SEO.js";
import Footer from "../components/Footer.js";
import RootPersonalizationModal from "../components/RootPersonalizedModal";
import HomeProfilePopupFlow from "../components/HomeProfilePopupFlow.js";
import {
  useGetViewBoardsQuery,
  usePostCreateBoardMutation,
  useProfileReadQuery,
} from "../store/api.ts";

const getApiErrorMessage = (error, fallbackMessage) => {
  return (
    error?.data?.detail ||
    error?.data?.message ||
    error?.data?.error ||
    error?.error ||
    fallbackMessage
  );
};

const formatUpdatedLabel = (dateValue) => {
  if (!dateValue) return "Updated recently";

  const updatedDate = new Date(dateValue);

  if (Number.isNaN(updatedDate.getTime())) {
    return "Updated recently";
  }

  const now = new Date();
  const diffMs = now.getTime() - updatedDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return "Updated now";
  if (diffMinutes < 60) return `Updated ${diffMinutes}m`;
  if (diffHours < 24) return `Updated ${diffHours}h`;
  if (diffDays < 7) return `Updated ${diffDays}d`;

  return `Updated ${updatedDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
};

const normalizeBoardsResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.boards)) return data.boards;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const BoardCover = ({ board }) => {
  const imageOne = board?.image_one;
  const imageTwo = board?.image_two;
  const imageThree = board?.image_three;

  const hasAnyImage = Boolean(imageOne || imageTwo || imageThree);

  if (!hasAnyImage) {
    return (
      <div className="grid h-full w-full grid-cols-2 grid-rows-2 overflow-hidden rounded-[3px]">
        <div className="bg-[#686969]" />
        <div className="row-span-2 bg-[#A0A1A1]" />
        <div className="bg-[#444545]" />
      </div>
    );
  }

  return (
    <div className="grid h-full w-full grid-cols-2 grid-rows-2 overflow-hidden rounded-[3px] bg-[#EFEFEF]">
      <div className="relative overflow-hidden bg-[#686969]">
        {imageOne ? (
          <img
            src={imageOne}
            alt={`${board?.name || "Board"} cover one`}
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>

      <div className="relative row-span-2 overflow-hidden bg-[#A0A1A1]">
        {imageTwo || imageOne ? (
          <img
            src={imageTwo || imageOne}
            alt={`${board?.name || "Board"} cover two`}
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>

      <div className="relative overflow-hidden bg-[#444545]">
        {imageThree || imageOne ? (
          <img
            src={imageThree || imageOne}
            alt={`${board?.name || "Board"} cover three`}
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>
    </div>
  );
};

const BoardCardSkeleton = () => {
  return (
    <div className="animate-pulse">
      <div className="aspect-[1.06/1] w-full rounded-[3px] bg-[#EFEFEF]" />

      <div className="mt-4 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="h-4 w-32 rounded bg-[#EFEFEF]" />
          <div className="mt-2 h-3 w-20 rounded bg-[#EFEFEF]" />
        </div>

        <div className="h-3 w-14 rounded bg-[#EFEFEF]" />
      </div>
    </div>
  );
};

const BOARDS_SEO_TITLE = "Your Fashion Boards | Eveness";
const BOARDS_SEO_DESCRIPTION =
  "Create and explore your saved fashion boards on Eveness. Organize outfits, products, and style inspiration in one place.";

const BoardPage = () => {
  const navigate = useNavigate();
  const { closeChatModal } = useMarketplaceChat();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createError, setCreateError] = useState("");

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("login");
  const [isHomeProfileFlowOpen, setIsHomeProfileFlowOpen] = useState(false);
  const [isRootPersonalizationModalOpen, setIsRootPersonalizationModalOpen] =
    useState(false);
  const [authTick, setAuthTick] = useState(0);

  const authToken = useMemo(() => {
    return localStorage.getItem("authToken");
  }, [authTick]);

  const isAuthenticated = Boolean(authToken);

  const {
    data: profileData,
    isSuccess: isProfileSuccess,
    isError: isProfileError,
    error: profileError,
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
    refetch: refetchProfile,
  } = useProfileReadQuery(undefined, {
    skip: !isAuthenticated,
  });

  const hasCompletedSurvey =
    isAuthenticated && isProfileSuccess && Boolean(profileData);

  const hasSignedInUserWithoutSurvey =
    isAuthenticated &&
    isProfileError &&
    (profileError?.status === 404 || profileError?.originalStatus === 404);

  const shouldShowSurveyPrompt =
    !isAuthenticated || hasSignedInUserWithoutSurvey;
  const canUseRealChat = hasCompletedSurvey;

  const isProfileChecking =
    isAuthenticated && (isProfileLoading || isProfileFetching);

  useEffect(() => {
    const handleAuthChanged = () => {
      setAuthTick((prev) => prev + 1);
    };

    const handleLoginCompleted = (event) => {
      setAuthTick((prev) => prev + 1);

      const shouldShowPersonalization =
        event?.detail?.shouldShowPersonalization ||
        event?.detail?.profileMissing;

      if (shouldShowPersonalization) {
        setIsAuthModalOpen(false);
        setIsRootPersonalizationModalOpen(true);
      }
    };

    window.addEventListener("eveness-auth-changed", handleAuthChanged);
    window.addEventListener("eveness-login-completed", handleLoginCompleted);

    return () => {
      window.removeEventListener("eveness-auth-changed", handleAuthChanged);
      window.removeEventListener(
        "eveness-login-completed",
        handleLoginCompleted,
      );
    };
  }, []);

  const openLoginModal = () => {
    setAuthModalMode("login");
    setIsAuthModalOpen(true);
  };

  const openSurveyGate = () => {
    closeChatModal();

    if (!isAuthenticated) {
      setIsRootPersonalizationModalOpen(true);
      return;
    }

    if (!hasCompletedSurvey) {
      setIsRootPersonalizationModalOpen(true);
    }
  };

  const startSurveyFlow = () => {
    setIsRootPersonalizationModalOpen(false);
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }

    setIsHomeProfileFlowOpen(true);
  };

  const handleHomeProfileComplete = async () => {
    setIsHomeProfileFlowOpen(false);
    setIsRootPersonalizationModalOpen(false);
    await refetchProfile?.();
    setAuthTick((prev) => prev + 1);
    window.dispatchEvent(new Event("eveness-auth-changed"));
  };

  const handleCloseRootPersonalizationModal = () => {
    setIsRootPersonalizationModalOpen(false);
  };

  const handleRootPersonalizationSignIn = () => {
    setIsRootPersonalizationModalOpen(false);
    openLoginModal();
  };

  const handleUnlockMatch = () => {
    startSurveyFlow();
  };
  const { data, isLoading, isFetching, isError, refetch } =
    useGetViewBoardsQuery();

  const [createBoard, { isLoading: isCreatingBoard }] =
    usePostCreateBoardMutation();

  const boards = normalizeBoardsResponse(data);

  const totalItems =
    typeof data?.total_pin_count === "number"
      ? data.total_pin_count
      : boards.reduce((sum, board) => sum + Number(board?.pin_count || 0), 0);

  const boardCount =
    typeof data?.count === "number" ? data.count : boards.length;

  const handleCreateBoard = async (boardName) => {
    setCreateError("");

    try {
      const createdBoard = await createBoard({
        boardCreate: {
          board_name: boardName,
        },
      }).unwrap();

      toast.success("Board created successfully.");

      setCreateModalOpen(false);

      await refetch();

      const createdBoardSlug = createdBoard?.slug;

      if (createdBoardSlug) {
        navigate(`/board/${createdBoardSlug}`, {
          state: {
            board: {
              ...createdBoard,
              pin_count: 0,
              image_one: null,
              image_two: null,
              image_three: null,
            },
          },
        });
      }
    } catch (error) {
      setCreateError(getApiErrorMessage(error, "Failed to create board."));
    }
  };

  return (
    <>
    <main className="min-h-screen bg-white pb-[60px]">
      <SEO
        title={BOARDS_SEO_TITLE}
        description={BOARDS_SEO_DESCRIPTION}
        path="/board"
        type="website"
      />
      <NewNavbar />
      {isAuthenticated ? (
        <>
          <RootPersonalizationModal
            isOpen={isRootPersonalizationModalOpen}
            variant={isAuthenticated ? "survey" : "guest"}
            onClose={handleCloseRootPersonalizationModal}
            onSignIn={handleRootPersonalizationSignIn}
            onUnlockMatch={handleUnlockMatch}
          />

          <AuthModal
            isOpen={isAuthModalOpen}
            initialMode={authModalMode}
            onClose={() => setIsAuthModalOpen(false)}
          />

          <HomeProfilePopupFlow
            isOpen={isHomeProfileFlowOpen}
            mode="restart"
            onClose={() => setIsHomeProfileFlowOpen(false)}
            onRestartComplete={handleHomeProfileComplete}
            isAuthenticated={isAuthenticated}
          />
        </>
      ) : null}
      <section className="mx-auto w-full max-w-[1540px] px-4 pt-5 md:px-10">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <p className="mb-2 font-spaceMono text-[11px] uppercase tracking-[0.18em] text-[#5C5C5C] font-normal">
              Your Collections
            </p>

            <h1 className="font-inter text-[34px] font-medium leading-none text-[#1E1E1E] md:text-[44px]">
              Boards
            </h1>
          </div>

          <div className="flex items-center gap-5">
            <p className="hidden font-spaceMono text-[11px] text-[#5C5C5C] md:block">
              {boardCount} boards · {totalItems} items
            </p>

            <button
              type="button"
              onClick={() => {
                setCreateError("");
                setCreateModalOpen(true);
              }}
              className="
                h-[34px] md:h-[40px] rounded-[5px] bg-[#8F333B] px-5
                font-inter text-[13px] font-medium text-white
                transition hover:opacity-[95%]
                ">
              + New board
            </button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-[8px] border border-[#E6E6E6] bg-[#FAFAFA] px-5 py-10 text-center">
            <h2 className="font-inter text-[18px] font-medium text-black">
              Failed to load boards
            </h2>

            <p className="mt-2 font-inter text-[14px] text-[#666]">
              Please try again.
            </p>

            <button
              type="button"
              onClick={refetch}
              className="mt-5 h-[38px] rounded-[5px] bg-[#313131] px-5 font-inter text-[13px] text-white"
            >
              Retry
            </button>
          </div>
        ) : isLoading || isFetching ? (
          <div className="grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <BoardCardSkeleton key={index} />
            ))}
          </div>
        ) : boards.length === 0 ? (
          <div className="rounded-[8px] border border-[#E6E6E6] bg-[#FAFAFA] px-5 py-14 text-center">
            <h2 className="font-inter text-[20px] font-medium text-black">
              No boards yet
            </h2>

            <p className="mt-2 font-inter text-[14px] text-[#666]">
              Saved products will appear here once you create or use a board.
            </p>

            <button
              type="button"
              onClick={() => {
                setCreateError("");
                setCreateModalOpen(true);
              }}
              className="
              mt-6 h-[38px] rounded-[5px] bg-[#8F333B] px-5
              font-inter text-[13px] font-medium text-white transition
              hover:opacity-[95%]
            "
            >
              Create your first board
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {boards.map((board) => (
              <Link
                key={board.slug || board.id}
                to={`/board/${board.slug || board.id}`}
                state={{ board }}
                className="group block"
              >
                <div className="aspect-[1.06/1] w-full overflow-hidden rounded-[5px] bg-[#EFEFEF]">
                  <BoardCover board={board} />
                </div>

                <div className="mt-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h2 className="truncate font-inter text-[16px] md:text-[20px] font-medium text-[#1E1E1E]">
                      {board.name || "Untitled board"}
                    </h2>

                    <p className="mt-2 font-spaceMono font-normal text-[10px] uppercase tracking-[0.12em] text-[#5C5C5C]">
                      {formatUpdatedLabel(board.updated_at)}
                    </p>
                  </div>

                  <p className="shrink-0 pt-1 font-spaceMono text-[11px] text-[#5C5C5C]">
                    {Number(board.pin_count || 0)} items
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <BoardNameModal
        isOpen={createModalOpen}
        mode="create"
        isSubmitting={isCreatingBoard}
        errorMessage={createError}
        onClose={() => {
          if (isCreatingBoard) return;
          setCreateModalOpen(false);
          setCreateError("");
        }}
        onSubmit={handleCreateBoard}
      />
      <FloatingChatBar
        onStartSurvey={openSurveyGate}
        onRequireProfile={openSurveyGate}
        canUseRealChat={canUseRealChat}
        shouldShowSurveyPrompt={shouldShowSurveyPrompt}
        isProfileChecking={isProfileChecking}
        isAuthenticated={isAuthenticated}
      />{" "}
    </main>
    <Footer />
    </>
  );
};

export default BoardPage;
